/**
 * Commentary panel.
 *
 * Reads from the canonical store keyed by `projectId` — no upload control.
 * If the project has no saved databook, the panel prompts the analyst to
 * build one from the FDD Toolbox first. Once a databook is saved, the
 * extracted tables load automatically and the analyst picks rows to include
 * in the commentary doc.
 */

import React, { useEffect, useState } from "react";
import type { CommentaryTable } from "../commentary-types";
import DatabookTableView from "./DatabookTableView";
import { apiFetch } from "../../api/fddClient";

type SheetTab = {
  id: string;
  label: string;
  endpoint: string;
};

const SHEET_TABS: SheetTab[] = [
  {
    id: "is-summary",
    label: "IS - Summary",
    endpoint: "/api/ppt/commentary/extract-is-summary",
  },
  // Future:
  // { id: "bs-stratified", label: "BS - Stratified", endpoint: "/api/ppt/commentary/extract-bs-stratified" },
];

type Status = "idle" | "loading" | "ready" | "error";

type Props = { projectId: string };

type SourceType = "afs" | "mgmt-meeting" | "other";
type SourceMeta = {
  id: string;
  type: SourceType;
  name: string;
  originalFilename: string;
  addedAt: string;
  charCount: number;
  fileExt: string;
};

const SOURCE_TYPE_OPTIONS: { value: SourceType; label: string }[] = [
  { value: "afs", label: "Audited financials" },
  { value: "mgmt-meeting", label: "Mgmt meeting notes" },
  { value: "other", label: "Other" },
];

const CommentaryPanel: React.FC<Props> = ({ projectId }) => {
  const [hasSavedDatabook, setHasSavedDatabook] = useState<boolean | null>(null);
  const [activeTabId, setActiveTabId] = useState<string>(SHEET_TABS[0].id);
  const [tableByTab, setTableByTab] = useState<Record<string, CommentaryTable>>({});
  const [statusByTab, setStatusByTab] = useState<Record<string, Status>>({});
  const [errorByTab, setErrorByTab] = useState<Record<string, string | null>>({});
  const [selectedByTab, setSelectedByTab] = useState<Record<string, Set<string>>>({});
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Sources subsystem state — AFS / mgmt meeting notes / other docs that
  // ground the commentary in real evidence. Each upload is parsed to plain
  // text server-side and stored alongside the saved databook.
  const [sources, setSources] = useState<SourceMeta[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [sourceUploadType, setSourceUploadType] = useState<SourceType>("afs");
  const [sourceUploadName, setSourceUploadName] = useState("");
  const [sourceUploading, setSourceUploading] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [sourceWarning, setSourceWarning] = useState<string | null>(null);

  // ── Check whether the project has a saved databook ─────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/api/databook-store/${encodeURIComponent(projectId)}/status`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { exists: boolean };
        if (!cancelled) setHasSavedDatabook(Boolean(data.exists));
      } catch {
        if (!cancelled) setHasSavedDatabook(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // ── Fetch sources for this project ─────────────────────────────
  const refreshSources = async () => {
    setSourcesLoading(true);
    setSourceError(null);
    try {
      const res = await apiFetch(`/api/ppt/commentary/sources?projectId=${encodeURIComponent(projectId)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { sources: SourceMeta[] };
      setSources(data.sources || []);
    } catch (err: any) {
      setSourceError(err?.message || "Failed to load sources.");
    } finally {
      setSourcesLoading(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    void refreshSources();
  }, [projectId]);

  const handleSourceUpload = async (file: File) => {
    setSourceError(null);
    setSourceWarning(null);
    setSourceUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("projectId", projectId);
      fd.append("type", sourceUploadType);
      if (sourceUploadName.trim()) fd.append("name", sourceUploadName.trim());

      const res = await apiFetch("/api/ppt/commentary/sources", { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      if (data.warning) setSourceWarning(data.warning);
      setSourceUploadName("");
      await refreshSources();
    } catch (err: any) {
      setSourceError(err?.message || "Upload failed.");
    } finally {
      setSourceUploading(false);
    }
  };

  const handleSourceDelete = async (id: string) => {
    setSourceError(null);
    try {
      const res = await apiFetch(`/api/ppt/commentary/sources/${encodeURIComponent(id)}?projectId=${encodeURIComponent(projectId)}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      await refreshSources();
    } catch (err: any) {
      setSourceError(err?.message || "Delete failed.");
    }
  };

  // ── Auto-fetch the active tab once we know a databook exists ───
  // Deps intentionally narrow: re-fetching when fetchTab's closure identity
  // changes would cause an infinite loop. Only re-run when the databook
  // appears or the user switches tabs.
  useEffect(() => {
    if (!hasSavedDatabook) return;
    if (tableByTab[activeTabId]) return; // already loaded
    void fetchTab(activeTabId);
  }, [hasSavedDatabook, activeTabId]);

  const fetchTab = async (tabId: string) => {
    const tab = SHEET_TABS.find((t) => t.id === tabId);
    if (!tab) return;
    setStatusByTab((s) => ({ ...s, [tabId]: "loading" }));
    setErrorByTab((e) => ({ ...e, [tabId]: null }));
    try {
      const res = await apiFetch(tab.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Extraction failed (HTTP ${res.status})`);
      }
      const table = (await res.json()) as CommentaryTable;
      setTableByTab((t) => ({ ...t, [tabId]: table }));
      setStatusByTab((s) => ({ ...s, [tabId]: "ready" }));
      // Auto-select rows flagged by the trending analytics. Only seed if
      // the tab's selection hasn't been touched yet.
      setSelectedByTab((prev) => {
        if (prev[tabId] !== undefined) return prev;
        const seeded = new Set<string>();
        for (const row of table.rows) {
          if (row.hasTrendingQuestion) seeded.add(row.rowKey);
        }
        return { ...prev, [tabId]: seeded };
      });
    } catch (err: any) {
      setErrorByTab((e) => ({ ...e, [tabId]: err?.message || "Extraction failed." }));
      setStatusByTab((s) => ({ ...s, [tabId]: "error" }));
    }
  };

  const handleTabClick = async (tabId: string) => {
    setActiveTabId(tabId);
    if (!tableByTab[tabId]) await fetchTab(tabId);
  };

  const toggleRow = (tabId: string, rowKey: string) => {
    setSelectedByTab((prev) => {
      const current = prev[tabId] ?? new Set<string>();
      const next = new Set(current);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return { ...prev, [tabId]: next };
    });
  };

  const clearSelection = (tabId: string) => {
    setSelectedByTab((prev) => ({ ...prev, [tabId]: new Set<string>() }));
  };

  const status = statusByTab[activeTabId] ?? "idle";
  const table = tableByTab[activeTabId];
  const error = errorByTab[activeTabId];
  const selectedKeys = selectedByTab[activeTabId] ?? new Set<string>();
  const totalSelectedAcrossTabs = Object.values(selectedByTab).reduce((n, s) => n + s.size, 0);

  // ── Word doc generation ─────────────────────────────────────────
  const handleGenerateDoc = async () => {
    if (totalSelectedAcrossTabs === 0) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      const allKeys: string[] = [];
      for (const set of Object.values(selectedByTab)) {
        for (const k of set) allKeys.push(k);
      }
      const res = await apiFetch("/api/ppt/commentary/generate-doc", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId, selectedRowKeys: allKeys }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Generation failed (HTTP ${res.status})`);
      }
      const blob = await res.blob();
      const fileName = parseFileNameFromHeaders(res.headers) || "PnL_Commentary.docx";
      triggerDownload(blob, fileName);
    } catch (err: any) {
      setGenerateError(err?.message || "Could not generate the Word document.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h3 style={headingStyle}>
          <span style={{ fontSize: 22 }}>💬</span>
          Commentary
        </h3>
        <div style={subheadStyle}>
          Reads from this project's saved databook. Pick the subsections you want commentary on, then generate the Word doc.
        </div>
      </div>

      {hasSavedDatabook === null && (
        <Section title="Loading" subtitle="Checking for a saved databook…">
          <div style={{ fontSize: 12, color: "#6b7280" }}>One moment.</div>
        </Section>
      )}

      {hasSavedDatabook === false && (
        <Section
          title="No databook saved yet"
          subtitle="The Commentary feature reads from this project's canonical store. Build a databook from the FDD Toolbox first."
        >
          <div style={emptyStyle}>
            Head to the <strong>FDD Toolbox</strong> tab and run the <strong>Databook</strong> builder. Once it finishes, this page will load automatically.
          </div>
        </Section>
      )}

      {hasSavedDatabook === true && (
        <Section
          title="Sources"
          subtitle="Add audited financials, management meeting notes, and other documents. Claude grounds the commentary in these — every driver in the final write-up traces back to a source."
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Upload row */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: 10, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <select
                value={sourceUploadType}
                onChange={(e) => setSourceUploadType(e.target.value as SourceType)}
                disabled={sourceUploading}
                style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12 }}
              >
                {SOURCE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Label (e.g., 'AFS FY25' or 'Mgmt meeting 2/14/26')"
                value={sourceUploadName}
                onChange={(e) => setSourceUploadName(e.target.value)}
                disabled={sourceUploading}
                style={{ flex: 1, minWidth: 220, padding: "4px 8px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: 12 }}
              />
              <label
                style={{
                  padding: "4px 12px", borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: sourceUploading ? "#9ca3af" : "#2f6b2f", color: "#fff",
                  cursor: sourceUploading ? "not-allowed" : "pointer",
                }}
              >
                {sourceUploading ? "Uploading…" : "Add source"}
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,.md,.csv"
                  disabled={sourceUploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleSourceUpload(f);
                    e.target.value = ""; // allow re-uploading the same file
                  }}
                  style={{ display: "none" }}
                />
              </label>
            </div>

            {sourceError && (
              <div style={{ padding: 8, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", fontSize: 12 }}>
                {sourceError}
              </div>
            )}
            {sourceWarning && (
              <div style={{ padding: 8, background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 6, color: "#92400e", fontSize: 12 }}>
                {sourceWarning}
              </div>
            )}

            {/* Source list */}
            {sourcesLoading && sources.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>Loading sources…</div>
            ) : sources.length === 0 ? (
              <div style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic", padding: "8px 0" }}>
                No sources yet. Add audited financials and meeting notes above so Claude has evidence to anchor the commentary.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {sources.map((s) => {
                  const typeLabel = SOURCE_TYPE_OPTIONS.find((o) => o.value === s.type)?.label || s.type;
                  const words = Math.round(s.charCount / 5); // crude word count
                  return (
                    <div
                      key={s.id}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "6px 10px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12,
                      }}
                    >
                      <span style={{ fontWeight: 600, color: "#2f6b2f", minWidth: 130 }}>{typeLabel}</span>
                      <span style={{ flex: 1 }}>{s.name}</span>
                      <span style={{ fontSize: 11, color: "#6b7280" }}>{s.originalFilename}</span>
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>~{words.toLocaleString()} words</span>
                      <button
                        onClick={() => void handleSourceDelete(s.id)}
                        style={{
                          background: "transparent", color: "#dc2626", border: "1px solid #fca5a5",
                          borderRadius: 4, padding: "1px 8px", fontSize: 11, cursor: "pointer",
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Section>
      )}

      {hasSavedDatabook === true && (
        <Section
          title="Extracted tables"
          subtitle="Tabs show each table pulled from the saved databook. Switch between them to review."
        >
          <div style={tabStripStyle}>
            {SHEET_TABS.map((t) => {
              const isActive = t.id === activeTabId;
              return (
                <button
                  key={t.id}
                  onClick={() => void handleTabClick(t.id)}
                  style={tabButtonStyle(isActive)}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {status === "loading" && (
            <div style={{ fontSize: 13, color: "#6b7280", padding: 8 }}>Loading {activeTabId}…</div>
          )}

          {status === "error" && error && <div style={errorStyle}>{error}</div>}

          {status === "ready" && table && (
            <>
              <div style={selectionBarStyle}>
                <span>
                  <strong>{selectedKeys.size}</strong> selected in this tab
                  {totalSelectedAcrossTabs > selectedKeys.size && (
                    <span style={{ color: "#6b7280", marginLeft: 8 }}>
                      ({totalSelectedAcrossTabs} across all tabs)
                    </span>
                  )}
                </span>
                {selectedKeys.size > 0 && (
                  <button onClick={() => clearSelection(activeTabId)} style={clearBtnStyle}>
                    Clear selection
                  </button>
                )}
              </div>
              <DatabookTableView
                table={table}
                selectedKeys={selectedKeys}
                onToggle={(rowKey) => toggleRow(activeTabId, rowKey)}
              />
            </>
          )}
        </Section>
      )}

      {/* ── Generate Word doc ───────────────────────────────────── */}
      {hasSavedDatabook === true && totalSelectedAcrossTabs > 0 && (
        <Section
          title="Generate commentary document"
          subtitle="Builds a Word doc with the four blocks (Key findings · Key trends · Trending questions · Summary paragraph) for each selected subsection."
        >
          {generateError && <div style={errorStyle}>{generateError}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={() => void handleGenerateDoc()}
              disabled={generating}
              style={{
                background: generating ? "#9bbf8f" : "#15803d",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "10px 16px",
                cursor: generating ? "not-allowed" : "pointer",
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              {generating
                ? "Generating…"
                : `📄 Generate Word document (${totalSelectedAcrossTabs} selected)`}
            </button>
          </div>
        </Section>
      )}
    </div>
  );
};

/* ─────────────────────────── small bits ─────────────────────────── */

function parseFileNameFromHeaders(headers: Headers): string | null {
  const cd = headers.get("Content-Disposition");
  if (!cd) return null;
  const m = cd.match(/filename\*?="?([^";]+)"?/i);
  return m ? decodeURIComponent(m[1]) : null;
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const headingStyle: React.CSSProperties = {
  margin: 0,
  color: "#2f6b2f",
  fontSize: 18,
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const subheadStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#4a7d4a",
  marginTop: 4,
};

const tabStripStyle: React.CSSProperties = {
  display: "flex",
  gap: 4,
  marginBottom: 12,
  borderBottom: "1px solid #cfe6c7",
};

function tabButtonStyle(isActive: boolean): React.CSSProperties {
  return {
    background: isActive ? "#ffffff" : "transparent",
    color: isActive ? "#15803d" : "#4a7d4a",
    border: "1px solid #cfe6c7",
    borderBottom: isActive ? "1px solid #ffffff" : "1px solid transparent",
    borderRadius: "6px 6px 0 0",
    padding: "6px 14px",
    cursor: "pointer",
    fontWeight: isActive ? 700 : 500,
    fontSize: 12,
    marginBottom: -1,
  };
}

const selectionBarStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  marginBottom: 8,
  background: "#f4fbf1",
  border: "1px solid #cfe6c7",
  borderRadius: 6,
  fontSize: 12,
  color: "#2f6b2f",
};

const clearBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "#6b7280",
  border: "1px solid #d1d5db",
  borderRadius: 4,
  padding: "2px 8px",
  fontSize: 11,
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  background: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12,
  color: "#991b1b",
};

const emptyStyle: React.CSSProperties = {
  background: "#fffbeb",
  border: "1px solid #fde68a",
  borderRadius: 8,
  padding: "12px 14px",
  fontSize: 13,
  color: "#78350f",
};

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid #cfe6c7",
      borderRadius: 8,
      padding: 12,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}
  >
    <div>
      <div style={{ fontWeight: 700, color: "#2f6b2f", fontSize: 13 }}>{title}</div>
      {subtitle && (
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{subtitle}</div>
      )}
    </div>
    {children}
  </div>
);

export default CommentaryPanel;
